// Archivo: src/pages/AdminServiceEdit.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../components/RichTextContent.css";
import { getPublicServiceById } from "../services/service.service";
import { updateService } from "../services/admin.service";

// Iconos
const BackIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

const SaveIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
    />
  </svg>
);

const UploadIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
    />
  </svg>
);

export default function AdminServiceEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Estados del formulario
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo: null,
  });
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [logoPreview, setLogoPreview] = useState(null);

  // Validar ID y cargar servicio
  useEffect(() => {
    const fetchService = async () => {
      if (!id || isNaN(parseInt(id))) {
        setError("ID de servicio inválido");
        setLoading(false);
        return;
      }

      try {
        const data = await getPublicServiceById(id);
        setService(data);
        setFormData({
          name: data.name || "",
          description: data.description || "",
          logo: null,
        });

        // Inicializar el editor con la descripción existente
        if (data.description) {
          const contentBlock = htmlToDraft(data.description);
          if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks,
            );
            setEditorState(EditorState.createWithContent(contentState));
          }
        }

        // Usar directamente logo_url del backend
        setLogoPreview(
          `${process.env.REACT_APP_STORAGE_URL}/${data.logo_path}`,
        );
      } catch (err) {
        setError("Error al cargar el servicio: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id]);

  // Manejar cambios en inputs de texto
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpiar mensajes al hacer cambios
    if (error) setError(null);
    if (successMessage) setSuccessMessage("");
  };

  // Manejar selección de logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith("image/")) {
        setError("Por favor selecciona un archivo de imagen válido");
        return;
      }

      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setError("El archivo es demasiado grande. Máximo 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        logo: file,
      }));

      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Limpiar errores
      setError(null);
    }
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones básicas
    if (!formData.name.trim()) {
      setError("El nombre del servicio es obligatorio");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      // Convertir el contenido del editor a HTML
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const htmlDescription = draftToHtml(rawContentState);

      // Crear FormData
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("description", htmlDescription);

      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }

      const response = await updateService(id, formDataToSend);

      setSuccessMessage("Servicio actualizado exitosamente");

      // // Actualizar datos locales con la respuesta
      // if (response.data) {
      //   setService(response.data);
      //   setLogoPreview(response.data.logo_url);
      // }

      // // Limpiar el archivo seleccionado
      // setFormData(prev => ({
      //   ...prev,
      //   logo: null
      // }));

      // // Limpiar el input de archivo
      // const fileInput = document.querySelector('input[type="file"]');
      // if (fileInput) fileInput.value = '';

      setTimeout(() => navigate("/admin"), 400);
    } catch (err) {
      setError(err.message || "Error al actualizar el servicio");
    } finally {
      setSaving(false);
    }
  };

  // Componente de loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gob-azul-medio mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando servicio...</p>
        </div>
      </div>
    );
  }

  // Componente de error sin servicio
  if (error && !service) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <Link
          to="/admin"
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <BackIcon />
          <span className="ml-2">Volver al panel</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editar Servicio</h1>
          <p className="text-gray-600 mt-1">
            Modifica la información del servicio: {service?.name}
          </p>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <BackIcon />
          <span className="ml-2">Volver</span>
        </Link>
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Formulario principal */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sección del logo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Logo del servicio
              </label>

              <div className="flex items-start space-x-6">
                {/* Preview del logo */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-300 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs text-center px-2">
                        Sin logo
                      </span>
                    )}
                  </div>
                </div>

                {/* Selector de archivo */}
                <div className="flex-grow">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors">
                    <UploadIcon />
                    <span className="ml-2">
                      {formData.logo ? "Cambiar logo" : "Seleccionar logo"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                      disabled={saving}
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Formatos: JPG, PNG, GIF. Máximo 5MB. Recomendado: 200x200px
                  </p>
                  {formData.logo && (
                    <p className="text-sm text-green-600 mt-1">
                      Nuevo logo: {formData.logo.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Nombre del servicio */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Nombre del servicio *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gob-azul-medio focus:border-transparent"
                placeholder="Ej: EDDI, SARH, etc."
                required
                disabled={saving}
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descripción
              </label>
              <div className="border border-gray-300 rounded-lg min-h-[200px]">
                <Editor
                  editorState={editorState}
                  onEditorStateChange={setEditorState}
                  wrapperClassName="wrapper-class"
                  editorClassName="editor-class px-3"
                  toolbarClassName="toolbar-class"
                  placeholder="Describe el servicio, sus funciones y beneficios..."
                  readOnly={saving}
                  toolbar={{
                    options: [
                      "inline",
                      "blockType",
                      "list",
                      "textAlign",
                      // "colorPicker",
                      // "link",
                      "remove",
                      "history",
                    ],
                    inline: {
                      options: ["bold", "italic", "underline", "strikethrough"],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ["Normal", "H1", "H2", "H3"],
                    },
                    list: {
                      options: ["unordered", "ordered"],
                    },
                  }}
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <Link
                to="/admin"
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={saving || !formData.name.trim()}
                className="inline-flex items-center px-6 py-3 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <SaveIcon />
                    <span className="ml-2">Guardar cambios</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Acciones rápidas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to={`/admin/servicios/${id}/secciones`}
            className="inline-flex items-center justify-center px-4 py-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            Gestionar Secciones
          </Link>

          <Link
            to={`/servicios/${id}`}
            target="_blank"
            className="inline-flex items-center justify-center px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            Ver página pública
          </Link>
        </div>
      </div>
    </div>
  );
}
